import { StateService } from '@uirouter/core';
import _ from 'lodash';
import {
  COOKIE_OPT_IN_ACCEPTED, COOKIE_OPT_IN_DECLINED, COOKIE_OPT_IN_PENDING, ONETRUST_CATEGORIES,
} from '../../constants';
import { LOGROCKET_ROOT_HOSTNAME, logRocketCookieNames } from './logrocket-cookies';

// Google Analytics writes `_ga`, `_gat` and `_gid` exactly, plus per-stream/per-tracker variants
// that append an underscore (`_ga_<streamId>`, `_gat_gtag_<id>`). Matching on the bare prefix
// would also sweep up unrelated cookies such as `_gateway_session`.
const GOOGLE_ANALYTICS_COOKIE_NAMES = ['_ga', '_gat', '_gid'];

function isGoogleAnalyticsCookie(name: string): boolean {
  return GOOGLE_ANALYTICS_COOKIE_NAMES.some((ga) => name === ga || name.startsWith(`${ga}_`));
}

class CookieAgreementService {
  $cookies: any;
  $state: StateService;
  $window: ng.IWindowService;
  UserService: any;
  ConfigService: any;
  $rootScope: any;
  constructor($cookies, UserService, ConfigService, $state: StateService, $window: ng.IWindowService, $rootScope) {
    this.$cookies = $cookies;
    this.UserService = UserService;
    this.ConfigService = ConfigService;
    this.$state = $state;
    this.$window = $window;
    this.$rootScope = $rootScope;
  }

  loadServices() {
    this._loadOneTrustScripts();
    const oneTrustAgreements = this._getOneTrustAgreements();
    let isFirstLoad = _.isEmpty(oneTrustAgreements);

    this.$rootScope.$watch(() => this.$cookies.get(this.$rootScope.oneTrustCookieName), (updatedCookie, previousCookie) => {
      // the agreement status is only one of several things that could change about the cookie, hence the need to clarify
      if (updatedCookie !== previousCookie && !_.isEqual(oneTrustAgreements, this._parseAgreements(updatedCookie))) {
        // this is to prevent a case in which the OneTrust cookie did not exist (incognito window, user has literally never accessed uTest before, etc.) and the OneTrust scripts eventually load async and the cookie suddenly exists (therefore being technically changed, even though the user did not do it).
        if (!isFirstLoad) {
          this.$window.location.reload();
        }
        isFirstLoad = false;
      }
    });

    // the mapping between categories and external services can be found in the OneTrust configuration (app.onetrust.com)
    if (this.hasAcceptedTargetingCookies()) {
      // this._loadMautic();
      this._loadEmbedly(); // Embedly does not set its own cookies, but embeds YouTube videos, which do. So we need to load it here.
    } else {
      this._deleteMauticCookies();
      this._deleteYoutubeCookies();
    }

    if (this.hasAcceptedPerformanceCookies()) {
      // these are not actually used for now, and are just a placeholder for when we re-add them.
      this._loadGoogleTagManager();
      this._loadMetaPixel();
    } else {
      this._deleteGoogleAnalyticsCookies();
      // LogRocket is gated on the same consent in app.module.ts, but it may have been granted
      // on an earlier visit -- clear anything it left behind so withdrawing consent really does
      // remove the cookies, not just stop new recording.
      this._deleteLogRocketCookies();
    }
  }

  agree() {
    const nowInOneYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
    this.$cookies.put(this.$rootScope.cookieOptInCookieName, 'true', { expires: nowInOneYear, domain: this.$rootScope.cookieDomain, secure: true });

    this._setServerSideCookieOptIn(true, true);
  }

  disagree() {
    this.$cookies.put(this.$rootScope.cookieOptInCookieName, 'false', { domain: this.$rootScope.cookieDomain });

    this._setServerSideCookieOptIn(false);
  }

  reset() {
    this.$cookies.remove(this.$rootScope.cookieOptInCookieName, { domain: this.$rootScope.cookieDomain, secure: true });

    this._setServerSideCookieOptIn(false, true);
  }

  hasAgreed() {
    const currentState = this._currentState();
    return currentState === COOKIE_OPT_IN_ACCEPTED;
  }

  isPending() {
    const currentState = this._currentState();
    return currentState === COOKIE_OPT_IN_PENDING;
  }

  hasAcceptedPerformanceCookies() {
    return this._getOneTrustAgreements()[ONETRUST_CATEGORIES.PerformanceCookies] === '1';
  }

  hasAcceptedTargetingCookies() {
    return this._getOneTrustAgreements()[ONETRUST_CATEGORIES.TargetingCookies] === '1';
  }

  _loadOneTrustScripts() {
    const id = 'onetrust-js';
    if (!document.getElementById(id)) {
      const oneTrustScriptTag = document.createElement('script');
      oneTrustScriptTag.id = id;
      oneTrustScriptTag.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      oneTrustScriptTag.setAttribute('type', 'text/javascript');
      oneTrustScriptTag.setAttribute('charset', 'UTF-8');
      oneTrustScriptTag.setAttribute('data-domain-script', this.$rootScope.oneTrustDataDomain);
      document.head.appendChild(oneTrustScriptTag);

      const oneTrustFunctionScriptTag = document.createElement('script');
      oneTrustFunctionScriptTag.type = 'text/javascript';
      oneTrustFunctionScriptTag.innerHTML = 'function OptanonWrapper() { }';
      document.head.appendChild(oneTrustFunctionScriptTag);
    }
  }

  _getOneTrustAgreements() {
    const oneTrustCookie = this.$cookies.get(this.$rootScope.oneTrustCookieName);
    return this._parseAgreements(oneTrustCookie);
  }

  _parseAgreements(oneTrustCookie) {
    const agreements = {};
    try {
      const decodedCookie = decodeURIComponent(oneTrustCookie);
      const groupString = decodedCookie.split('&').find((e) => e.startsWith('groups=')).split('=')[1];
      const groups = groupString.split(',');
      groups.forEach((group) => {
        const [key, value] = group.split(':');
        agreements[key] = value;
      });
    } catch (e) {
      console.debug(e);
    }
    return agreements;
  }

  _currentState() {
    const cookieValue = this.$cookies.get(this.$rootScope.cookieOptInCookieName, { domain: this.$rootScope.cookieDomain });

    if (cookieValue === 'true') {
      return COOKIE_OPT_IN_ACCEPTED;
    }

    if (cookieValue === 'false') {
      return COOKIE_OPT_IN_DECLINED;
    }

    return COOKIE_OPT_IN_PENDING;
  }

  _setServerSideCookieOptIn(optInBool, reload = false) {
    const { UserService, $window } = this;

    const loggedInUser = UserService.me();
    if (loggedInUser !== null) {
      UserService.updateMe({ cookie_opt_in: optInBool }).then(() => {
        if (reload === true) {
          $window.location.reload();
        }
      });
    }
  }

  _loadMautic() {
    (function (w: any, d, t, u, n, a, m) {
      w.MauticTrackingObject = n;
      /* eslint-disable */
      w[n] = w[n] || function () { (w[n].q = w[n].q || []).push(arguments); }, a = d.createElement(t),
      /* eslint-enable */
      m = d.getElementsByTagName(t)[0]; a.async = 1; a.src = u; m.parentNode.insertBefore(a, m);
    }(window, document, 'script', 'https://utest.mautic.net/mtc.js', 'mt'));
  }

  _loadEmbedly() {
    const d = document;
    const w: any = window;

    const id = 'embedly-platform',
      n = 'script';
    if (!d.getElementById(id)) {
      /* eslint-disable */
      w.embedly = w.embedly || function () { (w.embedly.q = w.embedly.q || []).push(arguments); };
      /* eslint-enable */
      const e = d.createElement(n); e.id = id; e.async = true;
      e.src = `${document.location.protocol === 'https:' ? 'https' : 'http'}://cdn.embedly.com/widgets/platform.js`;
      const s = d.getElementsByTagName(n)[0];
      s.parentNode.insertBefore(e, s);
    }
  }

  _deleteMauticCookies() {
    this.$cookies.remove('mautic_device_id', { domain: 'www.utest.com' });
    this.$cookies.remove('mtc_id', { domain: 'www.utest.com' });
    this.$cookies.remove('mtc_sid', { domain: 'www.utest.com' });
    this.$cookies.remove('mautic_device_id', { domain: 'www.m.utest.com' });
    this.$cookies.remove('mtc_id', { domain: 'www.m.utest.com' });
    this.$cookies.remove('mtc_sid', { domain: 'www.m.utest.com' });
  }

  _deleteGoogleAnalyticsCookies() {
    const allCookies = this.$cookies.getAll();
    const keys = Object.keys(allCookies).filter(isGoogleAnalyticsCookie);
    // Google Analytics writes on the registrable domain, which is what cookie_domain is set to.
    this._deleteCookies(keys, this.$rootScope.cookieDomain);
  }

  /**
   * Scoped to this environment's App ID rather than matched on a prefix: the App ID is part of
   * every LogRocket cookie name, so integration/stage never expires production's cookies on the
   * domain the two share.
   */
  _deleteLogRocketCookies() {
    if (!this.$rootScope.logRocketAppId) {
      return;
    }
    this._deleteCookies(logRocketCookieNames(this.$rootScope.logRocketAppId), LOGROCKET_ROOT_HOSTNAME);
  }

  /**
   * Removing without an explicit domain only expires a host-only cookie, which would leave the
   * real ones (written on the shared parent domain) in place -- clear both variants.
   */
  _deleteCookies(names: string[], domain: string) {
    names.forEach((name) => {
      this.$cookies.remove(name, { path: '/' });
      this.$cookies.remove(name, { path: '/', domain });
    });
  }

  _deleteYoutubeCookies() {
    this.$cookies.remove('VISITOR_INFO1_LIVE', { domain: '.youtube.com' });
    this.$cookies.remove('VISITOR_PRIVACY_METADATA', { domain: '.youtube.com' });
    this.$cookies.remove('YSC', { domain: '.youtube.com' });
    this.$cookies.remove('PREF', { domain: '.youtube.com' });
  }

  _loadGoogleTagManager() {}

  _loadMetaPixel() {}
}

CookieAgreementService.$inject = ['$cookies', 'UserService', 'ConfigService', '$state', '$window', '$rootScope'];

export default CookieAgreementService;
